/**
 * As pendências das atas — *"pendências viraram tarefa"* em forma de lista.
 *
 * **Um componente, dois públicos, e a diferença é declarada.** O recorte de quais pendências
 * existem é do servidor (`visibilidade.py` e a policy da `0013`); o que muda aqui é o que se
 * escreve ao redor de cada uma. Do lado interno entra a chave da issue e o motivo de o envio
 * estar pendente; do lado do cliente, não — a chave `ONB-…` é referência de um projeto onde
 * outros clientes também têm issue, e o servidor já a apaga (`mostra_vinculo_com_o_jira`). A tela
 * repete a regra em vez de confiar: é a segunda trava, e ela existe para o dia em que a primeira
 * falhar.
 *
 * **A tela também filtra por `visivel_ao_cliente`.** O servidor já filtrou; refiltrar aqui custa
 * uma linha e cobre a regressão que mais assusta — alguém troca o público numa rota e a lista do
 * cliente passa a receber o que é nosso.
 *
 * **A pendência FECHA, e é isso que a torna tarefa.** O botão de concluir é do lado interno
 * (`ata:editar`): quem confirma que a entrega aconteceu é quem conduz a implantação. Sem ele, a
 * pendência publicada entrava no cartão "o que está com você" do cliente e ficava lá para sempre
 * — o item congela ao publicar e nada emitia `PENDENCIA_RESOLVIDA`. O cliente vê a conclusão, não
 * a decreta.
 *
 * **`sem_tarefa` é pendência, nunca erro.** As credenciais do Jira estão vazias neste servidor de
 * propósito, e o portal **não cria** issue de pendência (ADR 0002: quem cria no ONB é a
 * Automation). O número existe para virar lista de trabalho; pintá-lo de vermelho ensinaria o
 * consultor a ignorar alarme, e no dia do erro de verdade ele não olharia.
 */

import { Aviso, Botao, Cartao, Chip } from "../ui";
import {
  ROTULO_DO_LADO,
  estadoDaTarefa,
  formatarPrazo,
  formatarQuando,
  rotuloDoTipoDeReuniao,
  type ListaDePendencias,
  type PendenciaDaAta as Pendencia,
} from "../../nucleo/reuniao";
import { MarcaDeDestino } from "./MarcaDeDestino";

export interface PropsDasPendenciasDaAta {
  pendencias: ListaDePendencias;
  /** De que lado esta lista está sendo lida. O cliente não vê Jira nem marca de destino. */
  publico: "cliente" | "interno";
  /** Fecha a pendência. Ausente = a tela só lê — é o caso do cliente. */
  aoConcluir?: (pendencia: Pendencia) => void;
  /** Qual está sendo concluída agora, para o botão não repetir o pedido. */
  concluindo?: string | null;
}

export function PendenciasDaAta({
  pendencias,
  publico,
  aoConcluir,
  concluindo,
}: PropsDasPendenciasDaAta) {
  const doCliente = publico === "cliente";
  /* A segunda trava, e ela é barata: o que o cliente lê é só o que atravessa a fronteira. */
  const lista = doCliente
    ? pendencias.pendencias.filter((item) => item.visivel_ao_cliente)
    : pendencias.pendencias;

  if (lista.length === 0) {
    return (
      <Aviso tom="marca" data-testid="sem-pendencias-de-ata">
        {doCliente
          ? "Nada ficou pendente das reuniões até agora."
          : "Nenhuma pendência saiu das atas desta implantação até agora."}
      </Aviso>
    );
  }

  return (
    <>
      {!doCliente ? (
        <p className="lead mb-3" data-testid="contagem-de-pendencias">
          <b>{pendencias.abertas}</b> {pendencias.abertas === 1 ? "aberta" : "abertas"} de{" "}
          <b>{lista.length}</b> {lista.length === 1 ? "pendência" : "pendências"} nas atas ·{" "}
          <b>{pendencias.sem_tarefa}</b> ainda sem tarefa no Jira. A tarefa de cada pendência é
          criada no Jira; aqui você cola a chave dela.
        </p>
      ) : null}

      {/* Os identificadores mudam com o público de propósito: um teste que procure a lista do
          cliente nunca pode achar, por acidente, a lista interna renderizada em outra tela. */}
      <div
        className="lista-de-itens"
        data-testid={doCliente ? "pendencias-do-cliente" : "pendencias-das-atas"}
      >
        {lista.map((pendencia) => (
          <CartaoDaPendencia
            key={pendencia.id}
            pendencia={pendencia}
            doCliente={doCliente}
            aoConcluir={aoConcluir}
            concluindo={concluindo === pendencia.id}
          />
        ))}
      </div>
    </>
  );
}

function CartaoDaPendencia({
  pendencia,
  doCliente,
  aoConcluir,
  concluindo,
}: {
  pendencia: Pendencia;
  doCliente: boolean;
  aoConcluir?: (pendencia: Pendencia) => void;
  concluindo: boolean;
}) {
  const tarefa = estadoDaTarefa(pendencia);
  const comOCliente = pendencia.responsavel_lado === "cliente";
  const concluida = pendencia.concluida_em !== null;

  return (
    <Cartao
      titulo={`${rotuloDoTipoDeReuniao(pendencia.reuniao_tipo)} · ${
        formatarQuando(pendencia.reuniao_em) ?? "—"
      }`}
      data-testid={
        doCliente ? `pendencia-do-cliente-${pendencia.id}` : `pendencia-da-ata-${pendencia.id}`
      }
      acessorio={
        concluida ? (
          <Chip estado="ok">concluída</Chip>
        ) : doCliente ? (
          <Chip estado={comOCliente ? "atencao" : "neutro"}>
            {comOCliente ? "com você" : "com a GEOvendas"}
          </Chip>
        ) : (
          <Chip estado={tarefa.estado === "vinculada" ? "ok" : "atencao"}>
            {tarefa.estado === "vinculada" ? tarefa.chave : "sem tarefa"}
          </Chip>
        )
      }
    >
      <p className="cartao__texto">{pendencia.texto}</p>

      <p className="nota-rodape mt-2">
        {pendencia.responsavel ?? "sem responsável nomeado"}
        {!doCliente && pendencia.responsavel_lado
          ? ` (${ROTULO_DO_LADO[pendencia.responsavel_lado]})`
          : ""}
        {pendencia.prazo ? (
          <>
            {" · até "}
            <span className="numero-tabular">{formatarPrazo(pendencia.prazo)}</span>
          </>
        ) : null}
        {!doCliente ? (
          <>
            {" · "}
            <MarcaDeDestino destino={pendencia.visivel_ao_cliente ? "cliente" : "interno"} />
          </>
        ) : null}
      </p>

      {concluida ? (
        <p className="nota-rodape mt-1" data-testid={`pendencia-concluida-${pendencia.id}`}>
          Cumprida em {formatarQuando(pendencia.concluida_em) ?? "—"}.
        </p>
      ) : null}

      {/* Concluir é ato interno e só existe depois da publicação — antes disso a pendência não
          virou tarefa de ninguém. O botão some quando já está cumprida: refazer o ato não muda
          nada e a linha do tempo já registrou o fechamento. */}
      {!doCliente && !concluida && aoConcluir ? (
        <div className="mt-2">
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            disabled={concluindo}
            onClick={() => aoConcluir(pendencia)}
            data-testid={`concluir-pendencia-${pendencia.id}`}
          >
            {concluindo ? "Concluindo…" : "Marcar como cumprida"}
          </Botao>
        </div>
      ) : null}

      {/* Do lado interno, o estado da tarefa por extenso. "Pendente" com o motivo do servidor, e
          nunca as palavras de defeito: o Jira desligado é o estado normal deste ambiente. */}
      {!doCliente && tarefa.estado === "pendente" ? (
        <p className="nota-rodape mt-1" data-testid="pendencia-do-jira">
          Tarefa pendente: {tarefa.motivo}
        </p>
      ) : null}
    </Cartao>
  );
}
